/* eslint-disable @typescript-eslint/no-explicit-any */
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { io } from 'socket.io-client';
import { api } from '../api/api';
import {
  getConversation,
  getConversationMessages,
  USER_CONVERSATION_ID,
} from '../api/conversations.api';
import { queryKeys } from '../api/queryKeys';
import {
  hasChatMessagesCache,
  hasUnseenOwnMessage,
  markChatMessagesSeen,
  replaceChatMessage,
  upsertChatMessage,
} from '../adminPages/Chat/chatCache';
import {
  bumpUserGroupRow,
  describeUserGroup,
  userThreadKind,
} from '../adminPages/Chat/groupsCache';
import {
  conversationHeading,
  isGroupConversation,
  messagePreview,
} from '../adminPages/Chat/helpers/chatMessage';
import { readReceiptEffect } from '../adminPages/Chat/helpers/chatReadReceipt';
import { systemMessageText } from '../adminPages/Chat/helpers/chatSystemMessage';
import {
  appendViktorDelta,
  discardViktorRun,
  hasViktorRunRow,
  resolveViktorRun,
  startViktorRun,
} from '../adminPages/Chat/viktorCache';
import { isAdmin, isPartner } from '../helpers/appType';
import {
  announceConversationMessage,
  shouldAnnounceMessage,
} from '../helpers/conversationNotification';
import { getRefreshToken } from '../helpers/localstorage-helper';
import { reportError } from '../helpers/reportError';
// The constants file itself, never the routes barrel: that one reaches the
// adminPages barrel and closes a cycle that renders a blank page with no error.
import { ADMIN_CHAT, CHAT_CONVERSATION_PARAM } from '../routes/adminConstants';
import { fetchConversationDetail } from '../adminPages/Chat/conversationTransport';
import { useAuth } from '../store/auth';
import { useChatSocketState } from '../store/useChatSocketState';
import { useViktorRunState } from '../store/viktorRun';
import {
  ConversationInboxUpdatedPayload,
  ConversationListItemResponse,
  ConversationType,
  ConversationMessageReadPayload,
  ConversationMessageWithStatus,
  ConversationResponse,
  AssistantThinkingPayload,
  ConversationTypingPayload,
  ConversationUpdatedPayload,
  ConversationUserMessageReadPayload,
  ViktorDeltaPayload,
  ViktorRunFinishedPayload,
  ViktorRunStartedPayload,
} from '../types/conversation.types';
import { SupportTicketUpdatedPayload } from '../types/supportTicket.types';

const SOCKET_URL = process.env.NX_API_URL;
/** Coalesce bursts - a busy inbox shouldn't cause one refetch per message. */
const INBOX_DEBOUNCE_MS = 500;
/** A `typing: true` with no follow-up expires on its own. */
const TYPING_TIMEOUT_MS = 5000;

/**
 * How long the assistant has to finish before the thread says it is working.
 *
 * Around two messages in five never reach the model at all - a rule answers
 * them - and those go from `reading` to `done` in a fraction of a second. Shown
 * at once, the line would be a flicker under the composer on every other
 * message, which reads as a glitch rather than as progress.
 */
const ASSISTANT_SHOW_DELAY_MS = 400;

/** Dropped after this without a word, in case `done` never arrives. */
const ASSISTANT_SAFETY_MS = 60000;
/**
 * Bursts of read receipts land together - a group where three people have the
 * thread open all answer the same message - and the refetch they cost is one
 * request per loaded page. Long enough to collapse a burst, short enough that
 * the tick still reads as live.
 */
const SEEN_REFRESH_DEBOUNCE_MS = 800;
/**
 * How far back to look for the message an event names, when the sender has to
 * be read off the message itself. Enough to survive a burst arriving between
 * the event and the request; not a page.
 */
const SENDER_LOOKUP_LIMIT = 5;
/** How far back a dispatcher thread is read to learn a person has it. */
const DISPATCHER_LOOKBACK = 50;
/**
 * How long a Viktor answer may run before the client stops believing in it.
 *
 * Viktor's own hard limit is 10 minutes per answer, and `viktor_run_finished`
 * is the only thing that ends a run on this side - a lost event would leave
 * Stop in place of Send for good. One minute past the limit the run is
 * declared over and the history refetched, which is where the answer is if it
 * was ever finished.
 */
const VIKTOR_RUN_TIMEOUT_MS = 11 * 60 * 1000;

/**
 * Whether any socket in this page's lifetime has connected yet.
 *
 * Module-level on purpose: the socket is torn down and rebuilt when the
 * refresh token rotates, and the gap between the old socket and the new one
 * loses events exactly like a reconnect does. Only the very first connect
 * after a page load has no gap behind it.
 */
let hasConnectedBefore = false;

type InboxPages = InfiniteData<ConversationListItemResponse[], unknown>;

/**
 * Whether the tab on screen is the one this row belongs in.
 *
 * The scope is the same string the inbox keys its query with: an audience for
 * the two support tabs, the joined types for the internal one (and for the
 * Merchants tab's Groups filter, which asks for `merchantGroup` alone).
 *
 * A merchant group lives in the Merchants tab although it is no support
 * thread - it says `merchantGroup` with a null audience - so it is matched on
 * its type before the audience rules, which would otherwise send a null
 * audience to the internal tab.
 *
 * Silent about it when the payload says neither - an older backend sends no
 * `supportAudience` and no `type`, and guessing wrong there would drop updates
 * on the floor. Better a needless refetch than a list that stops moving.
 */
const belongsToScope = (
  scope: string | null,
  payload: ConversationInboxUpdatedPayload
) => {
  /* A Viktor thread is in no list at all - the card above the inbox is its
     row, patched by the handler directly - so it belongs to no scope. */
  if (payload.type === 'viktor') return false;
  if (!scope) return true;
  /* Dispatcher threads have a tab to themselves, and nothing else is in it. */
  if (payload.type === 'dispatcher') return scope === 'dispatcher';
  if (scope === 'dispatcher') return false;
  if (payload.type === 'merchantGroup') {
    return scope === 'merchant' || scope === 'merchantGroup';
  }

  /* The Merchants tab narrowed to its groups. A support thread carries an
     audience, so only a payload that says nothing at all is let through. */
  if (scope === 'merchantGroup') {
    return !payload.type && !payload.supportAudience;
  }

  if (scope === 'merchant' || scope === 'sales') {
    if (payload.supportAudience) return payload.supportAudience === scope;
    // A direct or a group can never be in a support tab, whatever else is said.
    return payload.type ? payload.type === 'support' : true;
  }

  if (payload.type) return payload.type !== 'support';
  return !payload.supportAudience;
};

/** What a notification needs to say who wrote, and where. */
type ThreadDescription = {
  type: ConversationType;
  name: string;
  /** Participant names by user id - what turns a `senderId` into a person. */
  members: Record<number, string>;
};

/**
 * Which cache key a socket payload belongs to.
 *
 * On admin that is the conversation the event names. The merchant has one
 * support thread, reached through routes with no id and cached under a
 * sentinel - and any number of merchant groups, cached under their own ids.
 * Every event for either arrives on the same channel with nothing but the id
 * to tell them apart, which is `userThreadKind`'s job.
 *
 * Null when the merchant side cannot tell yet. The caller refetches rather
 * than writes: a group's message in the support thread would be the one wrong
 * guess everybody sees.
 */
const cacheIdFor = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: number,
  message?: ConversationMessageWithStatus,
  viewerId?: number | null
): number | string | null => {
  if (isAdmin) return conversationId;
  const kind = userThreadKind(queryClient, conversationId, message, viewerId);
  if (kind === 'group') return conversationId;
  return kind === 'support' ? USER_CONVERSATION_ID : null;
};

/**
 * Whether an id is the user's own Viktor thread, as far as this tab knows.
 *
 * Read off the row the Viktor tab fetched: nothing else in the app names that
 * conversation, and before the tab has ever been opened the thread cannot be
 * on screen either, so "unknown" and "no" are the same answer here.
 */
const isViktorConversation = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: number | string | null
) =>
  conversationId !== null &&
  queryClient.getQueryData<ConversationListItemResponse>(
    queryKeys.conversations.viktor
  )?.id === Number(conversationId);

/**
 * Live conversation updates for both apps.
 *
 * Mounted once, in `AdminLayout`, so the unread badge keeps updating while the
 * user is anywhere in the app - not only on the chat page.
 *
 * Gating happens in two layers, and the compile-time one has to come first:
 * the sales and brandOwner builds point at a different API with no
 * `/conversations` namespace, and socket.io would reconnect-loop forever
 * against it. The runtime layer (role / permission) is then the caller's
 * `enabled`.
 */
export const useConversationsSocket = ({ enabled }: { enabled: boolean }) => {
  const queryClient = useQueryClient();
  const socketRef = useRef<any>(null);
  const refreshToken = getRefreshToken();

  /*
   * Through a ref, like everything else the socket handlers touch: the effect
   * below is keyed on [active, refreshToken] alone, so a captured `navigate`
   * would go stale rather than re-subscribe.
   */
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  /** Who is reading, for "is this message mine" and for naming a direct. */
  const viewerId = useAuth((state) => state.user?.id) ?? null;
  const viewerRef = useRef(viewerId);
  viewerRef.current = viewerId;

  const observedConversationId = useChatSocketState(
    (state) => state.observedConversationId
  );
  const setInboxStale = useChatSocketState((state) => state.setInboxStale);
  const activeInboxKey = useChatSocketState((state) => state.activeInboxKey);
  const activeInboxScope = useChatSocketState(
    (state) => state.activeInboxScope
  );
  const setEmitTyping = useChatSocketState((state) => state.setEmitTyping);
  const setTypingUsers = useChatSocketState((state) => state.setTypingUsers);
  const setAssistantState = useChatSocketState(
    (state) => state.setAssistantState
  );

  /** Read inside socket handlers, which are created once per connection. */
  const observedRef = useRef<number | string | null>(observedConversationId);
  observedRef.current = observedConversationId;

  const inboxKeyRef = useRef(activeInboxKey);
  inboxKeyRef.current = activeInboxKey;

  const inboxScopeRef = useRef(activeInboxScope);
  inboxScopeRef.current = activeInboxScope;

  const inboxDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Same debounce, for the support tickets list - see the handler below. */

  const ticketListDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  /** Pending group `seen` refetch - see `refreshSeenFlags`. */
  const seenRefreshRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The clock on the Viktor answer in progress - see VIKTOR_RUN_TIMEOUT_MS. */
  const viktorWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Pending retry after a server-initiated disconnect - see the handler. */
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The wait before the assistant line appears, and its safety net. */
  const assistantShowRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assistantSafetyRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** userId -> the timer that drops them from the typing line. */
  const typingTimersRef = useRef(
    new Map<number, ReturnType<typeof setTimeout>>()
  );

  const active = enabled && (isAdmin || isPartner) && !!SOCKET_URL;

  const refreshBadge = useCallback(() => {
    if (inboxDebounceRef.current) clearTimeout(inboxDebounceRef.current);
    inboxDebounceRef.current = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.unreadCount,
      });
    }, INBOX_DEBOUNCE_MS);
  }, [queryClient]);

  /**
   * A new message reorders the inbox, which invalidates the keyset cursors
   * already issued for later pages - splicing blindly would make the next
   * `fetchNextPage` skip or repeat rows.
   *
   * So: only touch page 0, which has no incoming cursor and is therefore safe
   * to reorder. Anything else just flags the list as stale and lets the page
   * decide when to reset.
   *
   * It works against whichever key the page is showing, not the unfiltered one:
   * every filter combination caches separately, so writing into the default key
   * while someone is looking at "Groups" would leave their list frozen.
   *
   * When no key is published the chat page is not open - there is no list to
   * reorder and nothing to warn anybody about, so this does nothing at all.
   * That is what keeps the refresh pill from being armed by traffic that
   * arrived while the user was on another screen.
   */
  /**
   * The Viktor card's one row, kept current the way the inbox rows are - but
   * in place and unconditionally, since it is a single object under its own
   * key rather than a page of a keyset-paginated list. Nothing to do until the
   * chat page has fetched it once: the row does not exist before that, and
   * opening the page fetches it fresh anyway.
   */
  const applyViktorInboxUpdate = useCallback(
    (payload: ConversationInboxUpdatedPayload) => {
      queryClient.setQueryData<ConversationListItemResponse>(
        queryKeys.conversations.viktor,
        (row: ConversationListItemResponse | undefined) => {
          if (!row || row.id !== payload.conversationId) return row;
          const isOpen =
            String(observedRef.current) === String(payload.conversationId);
          return {
            ...row,
            lastMessageAt: new Date().toISOString(),
            lastMessagePreview: payload.preview,
            lastMessageSide: payload.side,
            /* Only Viktor's answers count as unread - a question is yours -
               and an answer landing in the open thread is read as it lands. */
            unread:
              payload.side === 'viktor' && !isOpen
                ? row.unread + 1
                : row.unread,
          };
        }
      );
    },
    [queryClient]
  );

  const applyInboxUpdate = useCallback(
    (payload: ConversationInboxUpdatedPayload) => {
      if (payload.type === 'viktor') {
        applyViktorInboxUpdate(payload);
        return;
      }
      const activeKey = inboxKeyRef.current;
      if (!activeKey) return;
      /* A row that lives in another tab cannot be reordered into this one, and
         flagging the list stale for it would make the tab you ARE looking at
         refetch for somebody else's message - three tabs, so twice as often as
         it used to be. */
      if (!belongsToScope(inboxScopeRef.current, payload)) return;

      const data = queryClient.getQueryData<InboxPages>(activeKey);

      const inFirstPage = data?.pages?.[0]?.some(
        (item) => item.id === payload.conversationId
      );

      if (!data || !inFirstPage) {
        setInboxStale(true);
        return;
      }

      queryClient.setQueryData<InboxPages>(
        activeKey,
        (current: InboxPages | undefined) => {
          if (!current) return current;
          const [first, ...rest] = current.pages;
          const existing = first.find(
            (item: ConversationListItemResponse) =>
              item.id === payload.conversationId
          );
          if (!existing) return current;

          /*
           * The event carries no timestamp - it arrives at creation time, so
           * the client clock is the closest thing available, and it only ever
           * affects the ordering of page 0, which this reorders anyway.
           */
          const now = new Date().toISOString();
          const isOpen =
            String(observedRef.current) === String(payload.conversationId);

          /*
           * Whose message this is, as far as the payload can say.
           *
           * `side` answers it in a support thread, where the two ends of the
           * conversation are vGrubs and the outside user. It answers nothing in
           * a direct or a group: there EVERY message is `internal`, yours and
           * your colleague's alike, so reading it that way marked all of them
           * as your own - which is why a group could take a message, move to
           * the top of the inbox with its new preview, and still show no unread
           * count while the sidebar badge (which the server counts) went up.
           *
           * For those, being open stands in: a message can only be sent from
           * the thread it belongs to, so anything arriving for one that is not
           * on screen came from somebody else. `isOpen` already suppresses the
           * count on its own, which is why this needs no sender id - and the
           * only case it misses is your own message sent from a second tab,
           * which `senderId` catches where the backend sends it, and the next
           * fetch corrects from the server where it does not.
           *
           * A merchant group is the same case with both sides in it - its
           * restaurants write as `user` - so it takes the same rule.
           */
          const isMine =
            existing.type === 'support'
              ? payload.side === (isPartner ? 'user' : 'internal')
              : !!payload.senderId && payload.senderId === viewerRef.current;

          const updated: ConversationListItemResponse = {
            ...existing,
            lastMessageAt: now,
            lastMessagePreview: payload.preview,
            lastMessageSide: payload.side,
            // Our own messages don't make a thread unread, and a thread that is
            // currently open is being read as it arrives.
            unread: isMine || isOpen ? existing.unread : existing.unread + 1,
            /* Same rule for the mention marker, and it never clears itself
               here: an older unread mention is still unread because this
               message arrived. */
            hasMention:
              isMine || isOpen
                ? false
                : existing.hasMention || !!payload.hasMention,
            // Only support threads have anyone waiting on an answer.
            unansweredSince:
              existing.type !== 'support'
                ? existing.unansweredSince
                : payload.side === 'user'
                ? existing.unansweredSince ?? now
                : null,
          };

          return {
            ...current,
            pages: [
              [
                updated,
                ...first.filter(
                  (item: ConversationListItemResponse) =>
                    item.id !== payload.conversationId
                ),
              ],
              ...rest,
            ],
          };
        }
      );
    },
    [queryClient, setInboxStale, applyViktorInboxUpdate]
  );

  /**
   * A merchant group's message, worded the way its push is: the group's name
   * over "Name: text", or "Name mentioned you: text".
   *
   * Every member's message counts here, the vGrubs team's and the other
   * restaurants' alike - a group is a room of people rather than a line to
   * support. Not your own, not a system line, and not while muted unless the
   * message names you, which is the push's rule too.
   *
   * The name comes from the Groups tab's rows when they are loaded, and from
   * the group itself when they are not - one request, cached under the same
   * key the open group reads, and only for a message worth ringing for.
   */
  const announceGroupMessage = useCallback(
    async (message: ConversationMessageWithStatus) => {
      const viewer = viewerRef.current;
      if (message.side === 'system') return;
      if (message.sender?.id != null && message.sender.id === viewer) return;
      if (!shouldAnnounceMessage(message.conversationId)) return;

      const id = message.conversationId;
      let group = describeUserGroup(queryClient, id);
      if (!group.name) {
        await queryClient
          .fetchQuery({
            queryKey: queryKeys.conversations.detail(id),
            queryFn: async () => (await fetchConversationDetail(id)).data.data,
          })
          .catch(() => undefined);
        group = describeUserGroup(queryClient, id);
      }

      const mention =
        viewer !== null && message.mentionUserIds.includes(viewer);
      if (group.muted && !mention) return;

      const who = message.sender?.name || 'Someone';
      announceConversationMessage({
        title: group.name || 'New group message',
        toastTitle: group.name || undefined,
        body: `${who}${mention ? ' mentioned you' : ''}: ${messagePreview(
          message
        )}`,
        conversationId: id,
        messageId: message.id,
        groupId: id,
        quietWhenObserving: id,
        data: { messageId: String(message.id) },
      }).catch(reportError);
    },
    [queryClient]
  );

  /**
   * Tell the merchant a message arrived, from the socket rather than the push.
   *
   * The socket is instant, has no per-conversation throttle, and hands us the
   * message itself - so the notification is ours to shape: it outlives a
   * banner, it collapses per conversation, and it deep-links. The push cannot
   * do any of that while the payload carries its own `notification` block,
   * which is what the browser displays for us before our code ever sees it.
   *
   * Whether the tab is visible or not. This used to stand down for a hidden
   * one, on the grounds that the push owns that case - but the push is allowed
   * to skip it: the backend sends at most one per conversation per five
   * minutes, and none at all for a message it considers read. A merchant with
   * the chat bubble open in a background tab therefore got nothing whatsoever,
   * which is the exact case that was reported. The two cannot double up
   * anyway - the service worker draws its notification with the same tag, so
   * whichever lands second replaces the first rather than stacking under it.
   *
   * Merchant only: an admin sits in the inbox with a badge in front of them,
   * and a notification per message across every support thread and group would
   * be unusable.
   *
   * Quiet only while THIS thread is being read: a merchant reading one of their
   * groups is exactly who a support answer should reach, and the other way
   * round. The support thread is observed under its sentinel, so that is what
   * it is matched on.
   */
  const announce = useCallback(
    (message: ConversationMessageWithStatus, cacheId: number | string) => {
      if (!isPartner) return;

      if (cacheId !== USER_CONVERSATION_ID) {
        announceGroupMessage(message).catch(reportError);
        return;
      }

      // The vGrubs side only. Your own message needs no announcing, and a system
      // row ("X joined") is not something to interrupt anybody with.
      if (message.side !== 'internal') return;

      announceConversationMessage({
        title: 'vGrubs Support',
        body: messagePreview(message),
        conversationId: message.conversationId,
        messageId: message.id,
        quietWhenObserving: USER_CONVERSATION_ID,
        data: { messageId: String(message.id) },
      }).catch(reportError);
    },
    [announceGroupMessage]
  );

  /**
   * What kind of conversation an id belongs to, what to call it, and who is in
   * it.
   *
   * The event says none of the three, so the conversation itself is asked -
   * once per conversation for as long as the tab lives, which is what the map
   * is for. `conversation_updated` drops the entry, since a rename or a new
   * member changes every field here.
   */
  const threadsRef = useRef(new Map<number, ThreadDescription>());

  const inboxRow = useCallback(
    (conversationId: number) => {
      const key = inboxKeyRef.current;
      if (!key) return undefined;
      const data = queryClient.getQueryData<InboxPages>(key);
      return data?.pages
        ?.flat()
        .find(
          (item: ConversationListItemResponse) => item.id === conversationId
        );
    },
    [queryClient]
  );

  /**
   * What KIND of conversation an id is, without asking the network.
   *
   * Read receipts need it and carry none: what a reader having reached a
   * message means depends entirely on the shape of the thread (see the
   * `message_read` handler). Three places already know, in descending order of
   * how sure they are - the detail the chat page fetches for an open group, the
   * description cached for the notifications, the inbox row on screen - and
   * between them the open thread is always covered.
   *
   * Undefined when none of them do, which the caller has to treat as the
   * expensive-but-correct case rather than guessing.
   */
  const conversationTypeOf = useCallback(
    (conversationId: number): ConversationType | undefined =>
      queryClient.getQueryData<ConversationResponse>(
        queryKeys.conversations.detail(conversationId)
      )?.type ??
      threadsRef.current.get(conversationId)?.type ??
      inboxRow(conversationId)?.type,
    [queryClient, inboxRow]
  );

  /**
   * Re-read `seen` from the server for the thread on screen.
   *
   * For a group the tick is all-or-nothing across every other active member,
   * and one member's read receipt cannot answer it: what the client would need
   * is each member's marker, and it is only ever told about the ones that move
   * while the tab is open. So the one place that can answer - the backend,
   * which computes `seen` per message on every read - is asked.
   *
   * Only for the observed thread: ticks are the only thing this changes and
   * they are only on screen there. Every other thread re-reads `seen` from the
   * server the next time it is opened anyway.
   *
   * And only when a tick could actually move. The backend emits a receipt for
   * every message anybody sends as well as for every read, so an active group
   * would otherwise refetch its whole loaded history once per message.
   *
   * `invalidateQueries`, not `resetQueries`: a reset drops the loaded history
   * and re-runs the open-scroll, which would throw the reader down to the
   * newest message because somebody else read theirs.
   */
  const refreshSeenFlags = useCallback(
    (conversationId: number | string, lastReadMessageId: number) => {
      if (String(observedRef.current) !== String(conversationId)) return;
      if (
        !hasUnseenOwnMessage(
          queryClient,
          conversationId,
          viewerRef.current,
          lastReadMessageId
        )
      )
        return;
      if (seenRefreshRef.current) clearTimeout(seenRefreshRef.current);
      seenRefreshRef.current = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.messages(conversationId),
        });
      }, SEEN_REFRESH_DEBOUNCE_MS);
    },
    [queryClient]
  );

  const describeThread = useCallback(
    async (
      payload: ConversationInboxUpdatedPayload
    ): Promise<ThreadDescription | undefined> => {
      const id = payload.conversationId;
      const known = threadsRef.current.get(id);
      if (known) return known;

      try {
        const { data } = await getConversation(id);
        const conversation = data.data;
        const described: ThreadDescription = {
          type: conversation.type,
          name:
            conversation.title ||
            conversation.participants
              .filter((member) => member.userId !== viewerRef.current)
              .map((member) => member.name)
              .join(', '),
          members: Object.fromEntries(
            conversation.participants.map((member) => [
              member.userId,
              member.name,
            ])
          ),
        };
        threadsRef.current.set(id, described);
        return described;
      } catch {
        /*
         * The inbox row, as a last resort rather than as the cheap path it
         * used to be: it names the thread but never its members, and naming
         * the sender is most of what the announcement is for. Not cached -
         * a request that failed once is worth trying again, and the row will
         * be gone as soon as the page changes its filter.
         */
        const row = inboxRow(id);
        return row
          ? { type: row.type, name: row.name, members: {} }
          : payload.type
          ? { type: payload.type, name: '', members: {} }
          : undefined;
      }
    },
    [inboxRow]
  );

  /**
   * Who wrote the message the event is about.
   *
   * `senderId` answers it against the member list - when the backend sends
   * one, which it does not yet. Failing that the message itself is asked for,
   * since it carries its sender: one request, on the announce path only, for
   * the handful of messages that are worth interrupting somebody for.
   *
   * A small page rather than exactly one row: another message can land between
   * the event and this request, and the wrong name on a notification is worse
   * than none.
   */
  const describeSender = useCallback(
    async (
      payload: ConversationInboxUpdatedPayload,
      thread: ThreadDescription | undefined
    ) => {
      if (payload.senderId) {
        const known = thread?.members[payload.senderId];
        if (known) return known;
      }

      const { data } = await getConversationMessages(payload.conversationId, {
        limit: SENDER_LOOKUP_LIMIT,
      });
      const message = data.data?.find((row) => row.id === payload.messageId);
      return message?.sender?.name ?? null;
    },
    []
  );

  /**
   * Tell an admin about a message they would otherwise only find by looking.
   *
   * The counterpart of `announce` above, and deliberately narrower. An admin
   * sits in front of an inbox that badges every unread thread, so announcing
   * merchant traffic across a shared support queue would be unusable noise.
   * What is worth interrupting for is the internal side: a direct message or a
   * group is addressed to a person rather than to a queue, and on the web there
   * is no push to fall back on - only the merchant app registers for one. A
   * mention announces itself wherever it happens.
   *
   * Driven by `inbox_updated`, because that is the only event that arrives for
   * a thread that is not open: `new_message` is emitted into the observed
   * conversation alone.
   *
   * "Not mine" is `senderId` when the backend sends it, and until then the
   * thread not being on screen: a message can only be sent from the
   * conversation it belongs to, so anything arriving for one you are not
   * looking at came from someone else. The gap is your own message sent from a
   * second tab, which announces itself there once.
   *
   * Sound and toast; a system notification too, if this browser happens to have
   * granted permission, which the admin app never asks for.
   */
  const announceIncoming = useCallback(
    async (payload: ConversationInboxUpdatedPayload) => {
      if (!isAdmin) return;
      if (payload.senderId && payload.senderId === viewerRef.current) return;
      /* Viktor's answers count on their own card and, if the thread stays
         unread, arrive as a push 15s later - an answer you asked for is not
         something to be interrupted with, and the describe lookup below would
         fire a GET per answer to learn that. */
      if (payload.type === 'viktor' || payload.side === 'viktor') return;

      /* The Dispatcher tab: the hand-over itself, and then every customer
         message once the thread is a person's (the AI handed it over or
         someone from the team wrote). Before that the AI is answering and
         nobody needs interrupting. The backend's push is
         held to one per thread every 5 minutes, so on the open admin this is
         what says each new line came in. */
      if (payload.type === 'dispatcher') {
        if (!shouldAnnounceMessage(payload.conversationId)) return;
        /* A customer's line needs the history; anything else only itself,
           so the AI's answers in a thread nobody has cost a small page. */
        const rows = await getConversationMessages(payload.conversationId, {
          limit: payload.side === 'user' ? DISPATCHER_LOOKBACK : 5,
        })
          .then(({ data }) => data.data ?? [])
          .catch(() => []);
        let body: string;
        if (payload.side === 'user') {
          const personOn = rows.some(
            (row) =>
              row.systemEvent === 'handedToHuman' ||
              (row.side === 'internal' && !row.isAssistant && !row.systemEvent)
          );
          if (!personOn) return;
          body = payload.preview || 'New message';
        } else {
          /* The hand-over itself: the customer pressed "Talk to a person" or
             the AI passed the thread on. Its row is the only one on the team's
             side worth a sound; the AI's answers and the team's own are not. */
          const row = rows.find((item) => item.id === payload.messageId);
          if (row?.systemEvent !== 'handedToHuman') return;
          body = systemMessageText(row, viewerRef.current) || 'Needs a person';
        }
        const thread = await describeThread(payload).catch(() => undefined);
        const heading = thread?.name || 'Customer';
        announceConversationMessage({
          title: heading,
          toastTitle: heading,
          body,
          conversationId: payload.conversationId,
          messageId: payload.messageId,
          quietWhenObserving: payload.conversationId,
          onOpen: () =>
            navigateRef.current(
              `${ADMIN_CHAT}?scope=dispatcher&${CHAT_CONVERSATION_PARAM}=${payload.conversationId}`
            ),
        }).catch(reportError);
        return;
      }

      /* Cheap exits before anything is looked up. An outside user's message is
         a support thread by definition - unless the payload says it is a
         group, where sales agents write as `user` too, and restaurants in a
         merchant group - and a thread being read needs no announcing. Both
         would otherwise cost a lookup to learn nothing. */
      if (
        payload.side === 'user' &&
        !isGroupConversation(payload.type) &&
        !payload.hasMention
      )
        return;
      if (!shouldAnnounceMessage(payload.conversationId)) return;

      const thread = await describeThread(payload).catch(() => undefined);
      /* Addressed to its members rather than to a queue, like a group - which
         is what it is, with restaurants in it. */
      const personal =
        thread?.type === 'direct' || isGroupConversation(thread?.type);
      if (!personal && !payload.hasMention) return;

      const sender = await describeSender(payload, thread).catch(() => null);

      const heading = conversationHeading({
        sender,
        thread,
        mention: payload.hasMention,
      });

      announceConversationMessage({
        title: heading,
        toastTitle: heading,
        body: payload.preview || 'New message',
        conversationId: payload.conversationId,
        messageId: payload.messageId,
        // Silent only while that very thread is open and in front - a message
        // elsewhere is exactly what this exists to interrupt.
        quietWhenObserving: payload.conversationId,
        /* Direct and group both live under the internal scope, and so does
           nearly every mention: a support thread has nobody to mention. A
           merchant group is the exception, listed with the merchants - the
           default tab, so no scope at all. */
        onOpen: () =>
          navigateRef.current(
            (thread?.type ?? payload.type) === 'merchantGroup'
              ? `${ADMIN_CHAT}?${CHAT_CONVERSATION_PARAM}=${payload.conversationId}`
              : `${ADMIN_CHAT}?scope=internal&${CHAT_CONVERSATION_PARAM}=${payload.conversationId}`
          ),
      }).catch(reportError);
    },
    [describeThread, describeSender]
  );

  /** Drop someone from the typing line, and cancel their pending timer. */
  const clearTypist = useCallback(
    (userId: number) => {
      const timer = typingTimersRef.current.get(userId);
      if (timer) clearTimeout(timer);
      typingTimersRef.current.delete(userId);
      setTypingUsers(
        useChatSocketState
          .getState()
          .typingUsers.filter((typist) => typist.userId !== userId)
      );
    },
    [setTypingUsers]
  );

  /** Take the assistant line down and cancel both of its timers. */
  const hideAssistant = useCallback(() => {
    if (assistantShowRef.current) clearTimeout(assistantShowRef.current);
    if (assistantSafetyRef.current) clearTimeout(assistantSafetyRef.current);
    assistantShowRef.current = null;
    assistantSafetyRef.current = null;
    setAssistantState(null);
  }, [setAssistantState]);

  useEffect(() => {
    if (!active || !refreshToken) return;

    const socket = io(SOCKET_URL, {
      path: '/conversations',
      transports: ['websocket'],
      auth: { authorization: refreshToken },
    });
    socketRef.current = socket;

    /** Re-subscribing here is what makes a cold page load work: opening a
     * thread races the handshake, and an emit before `connected` is silently
     * dropped by socket.io. */
    const subscribeToObserved = () => {
      if (!isAdmin) return; // The merchant is auto-joined to its own room.
      const conversationId = observedRef.current;
      if (conversationId === null) return;
      /* Viktor's events come to the personal room joined at connect, and
         nothing else is ever said in that thread - there is no room to ask
         for. The thread still publishes itself as observed, so the guards
         that read `observedRef` treat it like any other open thread. */
      if (isViktorConversation(queryClient, conversationId)) return;
      socket.emit('observe-conversation', {
        conversationId: Number(conversationId),
        subscribe: true,
      });
    };

    socket.on('connection_successful', subscribeToObserved);

    /*
     * Catch up after every gap in coverage.
     *
     * Any connect that is not the page's first has a gap behind it - a
     * network drop, a server hang-up, a socket rebuilt for a rotated token -
     * and the events of that gap are gone for good. Rather than trying to
     * gap-fill keyset cursors, drop the affected caches and let them refetch.
     *
     * On `connect`, NOT on the manager's `reconnect`, which this used to
     * hang off: `reconnect` only fires for socket.io's AUTOMATIC retries.
     * A server-initiated disconnect is recovered by the manual `connect()`
     * below and a token rotation builds a whole new socket - neither path
     * fires `reconnect`, and both used to leave the inbox permanently
     * behind while the unread badge (refetched on window focus) moved on
     * without it.
     */
    socket.on('connect', () => {
      if (!hasConnectedBefore) {
        hasConnectedBefore = true;
        return;
      }
      queryClient.resetQueries({ queryKey: queryKeys.conversations.inboxAll });
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.unreadCount,
      });
      /* The merchant's Groups tab, which the socket keeps by hand and the gap
         left behind. Invalidated, not reset: the list stays on screen while
         it refetches, and is usually one page. */
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.groupsAll,
      });
      /* Every thread, not just the open one: the gap swallowed events for all
         of them, and a cached history nobody refetches is how a message goes
         missing for the rest of the session. */
      queryClient.resetQueries({
        queryKey: queryKeys.conversations.messagesAll,
      });
      /* Ticket events went the same way as everything else in the gap, and the
         badge is the part nobody would notice was wrong. */
      if (isAdmin) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.supportTickets.all,
        });
      }
      /* The reset above dropped any half-streamed Viktor bubble with the
         rest of the history, and the run it belonged to is not resumable:
         the stream is held nowhere on the server, so the honest state is
         idle until a fresh `viktor_run_started` says otherwise. */
      endViktorRun();
      subscribeToObserved();
    });

    /**
     * The run is over on this side, whatever the server still has to say.
     * Clears the composer's Stop and the watchdog together, so neither can
     * outlive the other.
     */
    const endViktorRun = (runId?: string) => {
      if (viktorWatchdogRef.current) clearTimeout(viktorWatchdogRef.current);
      viktorWatchdogRef.current = null;
      const store = useViktorRunState.getState();
      if (runId === undefined) store.reset();
      else store.finishRun(runId);
    };

    /**
     * A run begins on this side: the indicator bubble, the composer's Stop,
     * and the clock that ends it if the server never does. `adopted` marks a
     * run first heard of mid-answer - see `ViktorRun`.
     */
    const beginViktorRun = (
      conversationId: number,
      runId: string,
      adopted = false
    ) => {
      endViktorRun();
      useViktorRunState.getState().startRun(conversationId, runId, adopted);
      startViktorRun(queryClient, conversationId, runId);

      viktorWatchdogRef.current = setTimeout(() => {
        endViktorRun(runId);
        discardViktorRun(queryClient, conversationId, runId);
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.messages(conversationId),
        });
      }, VIKTOR_RUN_TIMEOUT_MS);
    };

    /*
     * One of Viktor's answers, start to finish. See the payload types for the
     * order these arrive in; everything here is keyed on `runId` so a late
     * event from an interrupted answer cannot touch the one that replaced it.
     */
    socket.on('viktor_run_started', (payload: ViktorRunStartedPayload) =>
      beginViktorRun(payload.conversationId, payload.runId)
    );

    socket.on('viktor_delta', (payload: ViktorDeltaPayload) => {
      const { conversationId, runId, text } = payload;
      const run = useViktorRunState.getState().run;

      /*
       * A delta for a run this tab never saw start: the page was reloaded, or
       * the socket rebuilt, while Viktor was already writing. Adopting it
       * keeps the thread honest - Viktor IS writing, so the dots show and
       * Send is Stop - without showing text whose beginning is gone; the
       * stored answer arrives whole. The bubble is re-made per delta because
       * the history may still be refetching when the first one lands.
       */
      if (!run || (run.adopted && run.runId === runId)) {
        if (!run) beginViktorRun(conversationId, runId, true);
        else if (!hasViktorRunRow(queryClient, conversationId, runId)) {
          startViktorRun(queryClient, conversationId, runId);
        }
        return;
      }
      // Only the answer in progress grows; a delta from any other run is stale.
      if (run.runId !== runId) return;
      appendViktorDelta(queryClient, conversationId, runId, text);
    });

    socket.on('viktor_run_finished', (payload: ViktorRunFinishedPayload) => {
      const { conversationId, runId, status } = payload;
      endViktorRun(runId);

      if (status === 'cancelled') {
        discardViktorRun(queryClient, conversationId, runId);
        return;
      }
      /* Completed and failed both delivered their message as `new_message`
         before this, replacing the bubble. A bubble still standing means that
         event never reached us - so the stream is dropped and the stored
         answer fetched, rather than left as a half answer with no end. */
      if (hasViktorRunRow(queryClient, conversationId, runId)) {
        discardViktorRun(queryClient, conversationId, runId);
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.messages(conversationId),
        });
      }
    });

    socket.on('new_message', (message: ConversationMessageWithStatus) => {
      const cacheId = cacheIdFor(
        queryClient,
        message.conversationId,
        message,
        viewerRef.current
      );

      /* The merchant side could not tell the support thread from a group -
         see `cacheIdFor`. Both are asked again rather than either guessed. */
      if (cacheId === null) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.messages(USER_CONVERSATION_ID),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.groupsAll,
        });
        refreshBadge();
        return;
      }

      // On admin this can fire for a conversation that was never opened, whose
      // cache does not exist - writing into it would create a partial first
      // page that later paginates wrongly.
      if (!hasChatMessagesCache(queryClient, cacheId)) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.messages(cacheId),
        });
      } else if (message.side === 'viktor') {
        // Takes the place of the bubble the answer was streamed into.
        resolveViktorRun(queryClient, message.conversationId, message);
      } else {
        upsertChatMessage(queryClient, cacheId, message);
      }

      /* A merchant group's row moves to the top of the Groups tab with the new
         preview - the merchant gets every group message whole, so unlike the
         admin inbox there is nothing to wait for. A row this tab has never
         seen (a group the reader was just added to) has nothing to move, and
         the list is asked again instead. */
      if (!isAdmin && cacheId !== USER_CONVERSATION_ID) {
        const moved = bumpUserGroupRow(queryClient, message, {
          isOpen: String(observedRef.current) === String(cacheId),
          viewerId: viewerRef.current,
        });
        if (!moved) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.groupsAll,
          });
        }
      }

      // Somebody who was typing has now said it.
      if (message.sender?.id != null) clearTypist(message.sender.id);
      /* The bot has spoken, so it is no longer thinking. `done` follows this
         and would do the same, but the answer is the thing being waited for
         and the line should not outlive it by a round trip. Only for the
         thread on screen: on admin this event also fires for conversations
         nobody is looking at, and the line belongs to the open one. On the
         merchant side it belongs to the support thread, and a colleague's
         message in a group has nothing to do with it. */
      if (
        message.side === 'internal' &&
        (isAdmin
          ? String(observedRef.current) === String(cacheId)
          : cacheId === USER_CONVERSATION_ID)
      ) {
        hideAssistant();
      }
      refreshBadge();
      announce(message, cacheId);
    });

    socket.on('inbox_updated', (payload: ConversationInboxUpdatedPayload) => {
      applyInboxUpdate(payload);
      refreshBadge();
      announceIncoming(payload);

      /*
       * The message itself never reached us.
       *
       * `new_message` is only sent into the conversation being observed - this
       * event is all we get for every other thread. Its history is cached with
       * `staleTime: Infinity`, so without this the thread you open next would
       * be served straight from that cache and the message announced by the
       * badge and the inbox preview simply would not be there, until something
       * else happened to refetch it.
       *
       * Reset rather than invalidate: this list is paginated by keyset cursor,
       * and invalidating refetches every page that was ever loaded, in
       * sequence. A reset drops back to the newest page, which is where
       * opening a thread starts anyway.
       */
      const cacheId = cacheIdFor(queryClient, payload.conversationId);
      if (cacheId === null) return;
      if (String(observedRef.current) === String(cacheId)) return;
      queryClient.resetQueries({
        queryKey: queryKeys.conversations.messages(cacheId),
      });
    });

    /* Edit and soft delete both land here; the row keeps its slot either way.
       So do the assistant's own changes to a message - a card moving on, Yes /
       No buttons going dead. The quick replies spec calls that event
       `message_edited`, but on dev (2026-09-24) the same reminder job expired a
       card with `message_updated`, so there is no second event to listen for. */
    socket.on('message_updated', (message: ConversationMessageWithStatus) => {
      const cacheId = cacheIdFor(
        queryClient,
        message.conversationId,
        message,
        viewerRef.current
      );
      /* Unplaceable means no cache that could hold the message is loaded -
         the support thread's would have told it apart - so there is nothing
         to replace either. */
      if (cacheId === null) return;
      replaceChatMessage(queryClient, cacheId, message);
    });

    socket.on(
      'message_read',
      (
        payload:
          | ConversationMessageReadPayload
          | ConversationUserMessageReadPayload
      ) => {
        /*
         * On the merchant side a receipt is the support thread's unless it
         * is a group's, and a group's names its reader: a support receipt
         * never does, since one end of it is a collective. The caches decide
         * first where they can; the reader decides where they cannot.
         */
        const merchantGroupReceipt =
          !isAdmin &&
          'conversationId' in payload &&
          (userThreadKind(queryClient, payload.conversationId) === 'group' ||
            (userThreadKind(queryClient, payload.conversationId) ===
              'unknown' &&
              payload.readerUserId != null));

        const conversationId =
          'conversationId' in payload && (isAdmin || merchantGroupReceipt)
            ? payload.conversationId
            : USER_CONVERSATION_ID;

        const effect = readReceiptEffect({
          type:
            isAdmin && 'conversationId' in payload
              ? conversationTypeOf(payload.conversationId)
              : merchantGroupReceipt
              ? 'merchantGroup'
              : /* Otherwise the merchant's one support thread - whatever id
                   the payload names, and whether it names one at all. */
                'support',
          side: payload.side,
          readerUserId: payload.readerUserId,
          viewerId: viewerRef.current,
        });

        if (effect === 'ignore') return;
        if (effect === 'refresh') {
          refreshSeenFlags(conversationId, payload.lastReadMessageId);
          return;
        }
        markChatMessagesSeen(
          queryClient,
          conversationId,
          payload.lastReadMessageId
        );
      }
    );

    /** Members, title or image changed - the detail is no longer trustworthy. */
    socket.on('conversation_updated', (payload: ConversationUpdatedPayload) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(payload.conversationId),
      });
      // Including the copy the notifications name their threads from, which
      // would otherwise keep the old title for as long as the tab lives.
      threadsRef.current.delete(payload.conversationId);

      /* On the merchant side only a group changes this way, and the reader
         may have been added to it, removed from it, or have left it from
         another tab - the Groups tab is asked again whichever it was. An
         open group that the reader is no longer in finds out from its own
         detail, which now answers 403. */
      if (!isAdmin) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.groupsAll,
        });
      }

      /*
       * And the thread, when it is the one on screen.
       *
       * Every one of these changes also writes a system message into the
       * conversation - "X added Y", "X left" - and that message only ever
       * arrives as a `new_message`. If the server sends this event without one
       * (or without one to the member who caused it), the line simply never
       * appears until the thread is opened again. Refetching here makes the
       * thread right whichever way the backend chooses to announce it, and
       * costs one request per membership change.
       *
       * `invalidateQueries`, not `resetQueries`: a reset throws the loaded
       * history away and puts the query back in `pending`, which blanks the
       * conversation and re-runs its open-scroll. Invalidating leaves what is
       * on screen alone until the new pages land.
       */
      const observed = observedRef.current;
      if (
        observed !== null &&
        String(observed) === String(payload.conversationId)
      ) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.messages(observed),
        });
      }
      // The row shows the member list and the group name, so it is stale too -
      // but only worth saying so while somebody is looking at the list.
      if (inboxKeyRef.current) setInboxStale(true);
    });

    /*
     * An AI working on a message, phase by phase. One event, two assistants:
     * the support bot in a merchant thread and Viktor in his own. The first
     * branch decides which, because the two indicators share nothing but the
     * event - Viktor's words go in the bubble he is writing into, the bot's go
     * on the composer's line.
     *
     * Merchants are auto-joined to their own room and have exactly one thread,
     * so there is nothing to match there - `observedRef` holds a string
     * sentinel on that side and would never equal a real id. Admin gets the
     * event for the thread it has open, and has to check.
     */
    socket.on('assistant_thinking', (payload: AssistantThinkingPayload) => {
      /*
       * Viktor's phase follows none of the rules below. It appears at once:
       * the line under the composer has to wait 400ms because a rule may
       * answer a merchant in milliseconds, while Viktor's bubble is already
       * on screen and every question costs him seconds. And it has no 60s
       * net: he can be quiet for longer than that while he works with data,
       * so the fuse is the run's own watchdog. The phase carries no `runId`,
       * so it is kept beside the run, which clears it however it ends.
       */
      if (isViktorConversation(queryClient, payload.conversationId)) {
        useViktorRunState
          .getState()
          .setPhase(payload.state === 'done' ? null : payload.state);
        return;
      }

      /* A merchant group has no assistant, and its line would land on the
         support thread's composer - the only one this side keeps it for. */
      if (
        !isAdmin &&
        userThreadKind(queryClient, payload.conversationId) === 'group'
      )
        return;

      if (
        isAdmin &&
        String(observedRef.current) !== String(payload.conversationId)
      )
        return;

      if (payload.state === 'done') {
        hideAssistant();
        return;
      }

      if (assistantSafetyRef.current) clearTimeout(assistantSafetyRef.current);
      assistantSafetyRef.current = setTimeout(
        hideAssistant,
        ASSISTANT_SAFETY_MS
      );

      /* Already up: swap the words, do not restart the wait. A phase change is
         the same line saying something new, not a new line. */
      if (useChatSocketState.getState().assistantState !== null) {
        setAssistantState(payload.state);
        return;
      }

      if (assistantShowRef.current) clearTimeout(assistantShowRef.current);
      assistantShowRef.current = setTimeout(
        () => setAssistantState(payload.state),
        ASSISTANT_SHOW_DELAY_MS
      );
    });

    socket.on('typing', (payload: ConversationTypingPayload) => {
      // Only for the thread on screen - a typing line for a conversation you
      // are not looking at has nowhere to render.
      if (String(observedRef.current) !== String(payload.conversationId))
        return;

      const existing = useChatSocketState.getState().typingUsers;
      const timers = typingTimersRef.current;
      const running = timers.get(payload.userId);
      if (running) clearTimeout(running);

      if (!payload.typing) {
        timers.delete(payload.userId);
        setTypingUsers(
          existing.filter((typist) => typist.userId !== payload.userId)
        );
        return;
      }

      // Self-expiring: a tab closed mid-sentence never sends `typing: false`.
      timers.set(
        payload.userId,
        setTimeout(() => clearTypist(payload.userId), TYPING_TIMEOUT_MS)
      );

      if (existing.some((typist) => typist.userId === payload.userId)) return;
      setTypingUsers([
        ...existing,
        { userId: payload.userId, name: payload.name },
      ]);
    });

    /*
     * Come back when the SERVER hangs up.
     *
     * socket.io retries by itself after a network drop, but not after
     * `io server disconnect` - that reason means the other end closed the
     * connection deliberately, so the client treats it as final and stays
     * down until something recreates it. A deploy or a gateway restart
     * therefore left the tab with a dead socket and no sign of it: the badge
     * stopped moving, the inbox stopped reordering, and the open thread
     * stopped receiving, all silently, until a reload. (A bad token used to
     * arrive here too; the gateway now rejects it at the handshake, which is
     * the `connect_error` below.)
     *
     * Backing off rather than hammering: a server that dropped us once for a
     * reason of its own will drop us again, and the delay doubles to half a
     * minute rather than filling the log with attempts.
     */
    let serverDrops = 0;
    let authRejections = 0;
    socket.on('connect', () => {
      serverDrops = 0;
      authRejections = 0;
    });
    socket.on('disconnect', (reason: string) => {
      if (reason !== 'io server disconnect') return;
      serverDrops += 1;
      const delay = Math.min(2000 * 2 ** (serverDrops - 1), 30000);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => socket.connect(), delay);
    });

    socket.on('connect_error', (err: Error) => {
      console.error('Conversations socket connection error:', err.message);

      /*
       * Only the auth rejection is ours to recover; everything else - the
       * gateway down, the network gone - stays with socket.io's own retry
       * loop. `Unauthorized` is the handshake middleware refusing the token:
       * `connect` never fired and socket.io treats the refusal as final, so
       * without this the chat stays dead until a reload.
       */
      if (err.message !== 'Unauthorized') return;

      /*
       * The socket authenticates with the refresh token, so ask the auth
       * endpoint about that exact credential rather than guessing. Success
       * means the rejection was stale state - a token replaced by a re-login
       * in another tab, a mid-deploy blip - and a reconnect with whatever
       * storage holds NOW will stick. Failure means the session is over, and
       * by the time the catch runs the interceptor has already logged the
       * user out; reconnecting would only loop on a dead token, so stop.
       *
       * Same backoff as the disconnect path above: one bad answer usually
       * means more of them.
       */
      authRejections += 1;
      const delay = Math.min(2000 * 2 ** (authRejections - 1), 30000);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(async () => {
        try {
          await api.post('/v1/auth/refresh');
        } catch {
          return;
        }
        const freshest = getRefreshToken();
        if (!freshest) return;
        socket.auth = { authorization: freshest };
        socket.connect();
      }, delay);
    });

    /*
     * Hand the composer a way to broadcast without giving it the socket. Admin
     * only - the merchant API has no typing channel, and emitting into it would
     * just be ignored.
     */
    if (isAdmin) {
      setEmitTyping((conversationId, typing, audience) => {
        if (!socket.connected) return;
        socket.emit('typing', {
          conversationId: Number(conversationId),
          typing,
          ...(audience ? { audience } : {}),
        });
      });

      /*
       * A support ticket moved: the triage bot opened one, the merchant
       * answered it, or a colleague claimed, released or resolved it. Everyone
       * with the support inbox gets this without subscribing to anything.
       */
      socket.on(
        'support_ticket_updated',
        ({ ticket, openCount }: SupportTicketUpdatedPayload) => {
          // The badge rides along with every event, so it is never a request.
          queryClient.setQueryData(queryKeys.supportTickets.openCount, {
            open: openCount,
          });
          // An open detail is the one place worth updating in place.
          queryClient.setQueryData(
            queryKeys.supportTickets.detail(ticket.id),
            ticket
          );
          /* Approving a price, the merchant picking a brand or adding a
             price, and an order being attached all come as this event, and
             each can change the dishes offered. Only an open ticket's list is
             mounted, so this is no request for any other. */
          queryClient.invalidateQueries({
            queryKey: queryKeys.supportTickets.menuPriceFixes(ticket.id),
          });

          /*
           * The lists are refetched rather than patched, which is the opposite
           * of what the inbox does with `applyInboxUpdate`. Membership of a tab
           * is now decidable client side - the ticket carries `claimedById` -
           * but the order is not: it is a server sort, status first and then
           * oldest, so an inserted row would sit in the wrong place until the
           * next fetch. A refetch is one request and always right.
           *
           * Debounced because claiming a ticket is two events in a row, and
           * tickets arrive at human speed regardless.
           */
          if (ticketListDebounceRef.current) {
            clearTimeout(ticketListDebounceRef.current);
          }
          ticketListDebounceRef.current = setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.supportTickets.listAll,
            });
          }, INBOX_DEBOUNCE_MS);
        }
      );
    }

    return () => {
      if (inboxDebounceRef.current) clearTimeout(inboxDebounceRef.current);
      if (ticketListDebounceRef.current) {
        clearTimeout(ticketListDebounceRef.current);
      }
      if (seenRefreshRef.current) clearTimeout(seenRefreshRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      // The socket being rebuilt is a gap like any other - see `connect`.
      endViktorRun();
      typingTimersRef.current.forEach((timer) => clearTimeout(timer));
      typingTimersRef.current.clear();
      hideAssistant();
      setEmitTyping(null);
      setTypingUsers([]);
      socket.disconnect();
      socketRef.current = null;
    };
    // Intentionally keyed only on [active, refreshToken]: the handlers read
    // mutable state through refs, so re-running this would tear down a healthy
    // socket on every render.
  }, [active, refreshToken]);

  /**
   * Catch up the moment the page is LOOKED AT with a dead socket, not only
   * when the socket notices.
   *
   * A phone that locks or backgrounds the browser freezes this tab, and the
   * server drops the frozen connection long before the tab runs again. On
   * resume, socket.io can sit out most of a minute of ping timeout before it
   * notices and reconnects (which runs the catch-up above) - and in that
   * window the unread badge refetches on focus while the inbox, which only
   * the socket keeps current, still shows the world from before the pocket.
   * That exact mismatch is what got reported: a count on the tab with no
   * unread row under it.
   *
   * So when the tab turns visible and the socket is down, flag everything a
   * live socket would have kept fresh. The inbox goes through the same stale
   * flag the socket uses - keeping its keyset-cursor and deep-scroll rules -
   * the badge query refetches, and the open thread revalidates, which is
   * also what moves the merchant's read ticks: those only ever move on
   * socket events.
   *
   * `online` too: coming back from airplane mode is the same blindness with
   * the same recovery, and it can happen without a visibility change.
   */
  useEffect(() => {
    if (!active) return undefined;

    const catchUpIfBlind = () => {
      if (document.visibilityState !== 'visible') return;
      if (socketRef.current?.connected) return;

      setInboxStale(true);
      refreshBadge();
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.groupsAll,
      });
      // Same blindness, same recovery - see the connect handler.
      if (isAdmin) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.supportTickets.all,
        });
      }

      const observed = observedRef.current;
      if (observed !== null) {
        // Invalidate, not reset: a reset blanks the conversation on screen.
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.messages(observed),
        });
      }
    };

    document.addEventListener('visibilitychange', catchUpIfBlind);
    window.addEventListener('online', catchUpIfBlind);
    return () => {
      document.removeEventListener('visibilitychange', catchUpIfBlind);
      window.removeEventListener('online', catchUpIfBlind);
    };
  }, [active, setInboxStale, refreshBadge, queryClient]);

  /** Subscribe / unsubscribe as the user opens and closes threads. */
  useEffect(() => {
    if (!isAdmin) return;
    const socket = socketRef.current;
    if (!socket?.connected) return;
    // No room for the Viktor thread - see `subscribeToObserved`.
    if (isViktorConversation(queryClient, observedConversationId)) return;

    if (observedConversationId !== null) {
      socket.emit('observe-conversation', {
        conversationId: Number(observedConversationId),
        subscribe: true,
      });
    }

    return () => {
      if (observedConversationId !== null && socket?.connected) {
        socket.emit('observe-conversation', {
          conversationId: Number(observedConversationId),
          subscribe: false,
        });
      }
    };
  }, [observedConversationId, queryClient]);
};
